'use client';

import { usePathname } from 'next/navigation';
import type React from 'react';
import { useEffect, useRef } from 'react';
import { createContext, useContextSelector } from 'use-context-selector';
import type { ImmerHook } from 'use-immer';
import { useImmer } from 'use-immer';
import { z } from 'zod';

import { useChatAvailabilityQuery } from '@/core/components/useChatAvailabilityQuery';
import type {
  AgentStepProgress,
  ChatScopeInputValue,
  CreateChatPromptResult,
} from '@/infrastructures/orpcBackend/routers/chats.schema';

export interface State {
  storageKey?: string;
  materialLayoutId?: string;
  isOpen: boolean;
  isEmbeddedMode: boolean;
  isHistoryOpen: boolean;
  chatId?: string;
  prompts: CreateChatPromptResult['data'][];
  pendingPrompt?: Omit<CreateChatPromptResult['data'], 'id' | 'chatId' | 'model'> & {
    requestId: string;
    agentSteps: AgentStepProgress[];
  };
}

type Value = [State & { isReady: boolean }, ImmerHook<State>[1]];

const context = createContext<Value | undefined>(undefined);

export function useChatPaneSelector<Selected>(selector: (value: Value) => Selected): Selected {
  return useContextSelector(context, (value) => {
    if (!value) throw new Error('`ChatPaneProvider` not found');
    return selector(value);
  });
}

const coursePagePattern = /^\/[^/]+\/courses\/([^/]+)$/;
// Every page under a material (its body, editor, review, and survey management) shares the material scope.
const materialPagePattern = /^\/[^/]+\/materials\/([^/]+)(?:\/|$)/;
const problemPagePattern = /^\/[^/]+\/materials\/([^/]+)\/problems\/([^/]+)$/;

/** Returns the chat scope of the given page path: the course, material, or problem page it shows. */
export function getChatScopeFromPathname(pathname: string): ChatScopeInputValue | undefined {
  const courseMatch = coursePagePattern.exec(pathname);
  if (courseMatch?.[1]) return { courseId: decodeURIComponent(courseMatch[1]) };
  const problemMatch = problemPagePattern.exec(pathname);
  if (problemMatch?.[1] && problemMatch[2]) {
    return { materialId: decodeURIComponent(problemMatch[1]), problemId: decodeURIComponent(problemMatch[2]) };
  }
  const materialMatch = materialPagePattern.exec(pathname);
  if (materialMatch?.[1]) return { materialId: decodeURIComponent(materialMatch[1]) };
  return undefined;
}

export function useChatScope(): ChatScopeInputValue | undefined {
  return getChatScopeFromPathname(usePathname());
}

export const ChatPaneProvider: React.FC<{ children: React.ReactNode }> = (props) => {
  const immerHook = useImmer<State>({
    isOpen: false,
    isEmbeddedMode: false,
    isHistoryOpen: false,
    prompts: [],
  });
  const [chatState, setChatState] = immerHook;
  const pathname = usePathname();
  const scope = getChatScopeFromPathname(pathname);
  const { query: availabilityQuery } = useChatAvailabilityQuery(scope);
  const isPending = availabilityQuery.isPending;
  const userId = availabilityQuery.data?.userId;
  const userStorageKey = userId === undefined ? undefined : `exercode.chatPane.${userId}`;
  const scopeKey = scope ? `${scope.courseId ?? ''}/${scope.materialId ?? ''}/${scope.problemId ?? ''}` : '';
  const storageKey = userStorageKey ? `${userStorageKey}.selection.${scopeKey}` : undefined;
  const previousUserStorageKeyRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (isPending || (chatState.storageKey === storageKey && previousUserStorageKeyRef.current === userStorageKey))
      return;
    const hasUserChanged = previousUserStorageKeyRef.current !== userStorageKey;
    const savedSelection = storageKey ? readStorage(storageKey, z.union([z.uuid(), z.literal('new')])) : undefined;
    const savedIsOpen = userStorageKey ? readStorage(`${userStorageKey}.isOpen`, z.boolean()) : undefined;
    setChatState((draft) => {
      draft.storageKey = storageKey;
      if (hasUserChanged) draft.isOpen = savedIsOpen ?? false;
      draft.chatId = savedSelection === 'new' ? undefined : savedSelection;
      draft.prompts = [];
      draft.pendingPrompt = undefined;
      draft.isHistoryOpen = false;
    });
    previousUserStorageKeyRef.current = userStorageKey;
  }, [isPending, storageKey, userStorageKey, chatState.storageKey, setChatState]);

  useEffect(() => {
    // Wait for this user's scope to be restored before saving the rendered selection.
    if (isPending || !storageKey || chatState.storageKey !== storageKey || !userStorageKey) return;
    try {
      globalThis.localStorage.setItem(`${userStorageKey}.isOpen`, JSON.stringify(chatState.isOpen));
      if (scopeKey) globalThis.localStorage.setItem(storageKey, JSON.stringify(chatState.chatId ?? 'new'));
    } catch {
      // Browser storage can be disabled; the in-memory chat remains usable.
    }
  }, [isPending, storageKey, userStorageKey, scopeKey, chatState.storageKey, chatState.isOpen, chatState.chatId]);

  const hasMaterialLayout =
    !/^\/[^/]+\/materials\/[^/]+\/?$/.test(pathname) || chatState.materialLayoutId === scope?.materialId;
  const isReady = !isPending && !!userStorageKey && chatState.storageKey === storageKey && hasMaterialLayout;
  return <context.Provider value={[{ ...chatState, isReady }, setChatState]}>{props.children}</context.Provider>;
};

function readStorage<T>(key: string, schema: z.ZodType<T>): T | undefined {
  try {
    const raw = globalThis.localStorage.getItem(key);
    return raw === null ? undefined : schema.safeParse(JSON.parse(raw)).data;
  } catch {
    return undefined;
  }
}
